import { SxProps, Theme } from "@mui/material";
import { colors } from "../../colors/colors";

export const loginContainerSx: SxProps<Theme> = (theme) => ({
  height: "100dvh",
  minHeight: "100dvh",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.loginBg,
  overflowY: "auto",
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
  "@media (orientation: landscape) and (max-height: 650px)": {
    alignItems: "flex-start",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
});

export const paperSx: SxProps<Theme> = (theme) => ({
  "--Paper-overlay": "none !important",
  position: "relative",
  width: "100%",
  maxWidth: 500,
  borderRadius: "32px",
  backgroundColor: theme.palette.panelInfo.panel.bg,
  p: "48px",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "24px",
    padding: theme.spacing(3),
  },
  "@media (orientation: landscape) and (max-height: 650px)": {
    borderRadius: "20px",
    padding: theme.spacing(2),
  },
});

export const titleSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.header.titalColor,
  marginBottom: "16px",
  fontFamily: '"Red Hat Display", sans-serif',
  fontWeight: 600,
  fontSize: "32px",
  lineHeight: 1.334,
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
  "@media (orientation: landscape) and (max-height: 650px)": {
    fontSize: "20px",
    marginBottom: theme.spacing(1),
  },
});

export const formSx: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  "@media (orientation: landscape) and (max-height: 650px)": {
    gap: 1,
  },
});

export const labelSx =
  (valid: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    color: valid ? "#06B6D4" : colors.error,
    fontSize: "18px",
    fontWeight: 600,
    "@media (orientation: landscape) and (max-height: 650px)": {
      fontSize: "16px",
    },
  });

export const passwordBoxSx: SxProps<Theme> = {
  position: "relative",
};

export const secondaryButtonSx =
  (loading: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    textTransform: "none",
    borderRadius: "999px",
    py: "16px",
    cursor: loading ? "not-allowed" : "pointer",
    "@media (orientation: landscape) and (max-height: 650px)": {
      py: "10px",
      minHeight: 40,
    },
  });

export const buttonSubmitSx =
  (loading: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    mt: 2,
    textTransform: "none",
    py: "16px",
    fontWeight: "bold",
    background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
    color: "#ffffff",
    borderRadius: "999px",
    "&:hover": {
      background: "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
      color: "#ffffff",
    },
    cursor: loading ? "not-allowed" : "pointer",
    "@media (orientation: landscape) and (max-height: 650px)": {
      mt: 1,
      py: "10px",
      minHeight: 44,
    },
  });

export const textFieldSx =
  (valid: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    "& .MuiOutlinedInput-root": {
      marginTop: "8px",
      borderRadius: "999px",
      backgroundColor: theme.palette.input.bgColor,
      borderColor: !valid ? colors.error : undefined,
      borderWidth: !valid ? "1px" : undefined,
      overflow: "hidden",

      "& .MuiOutlinedInput-input": {
        "@media (orientation: landscape) and (max-height: 650px)": {
          paddingTop: "10px",
          paddingBottom: "10px",
          fontSize: "16px",
        },
        "&:-webkit-autofill": {
          boxShadow: "0 0 0 1000px transparent inset !important",
          WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
          WebkitTextFillColor: theme.palette.text.primary + " !important",
          animationName: "none !important",
          animationDuration: "0s !important",
          animationDelay: "0s !important",
          transition: "background-color 99999s ease-in-out 0s !important",
        },
        "&:-webkit-autofill:hover, &:-webkit-autofill:focus": {
          boxShadow: "0 0 0 1000px transparent inset !important",
          WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
          WebkitTextFillColor: theme.palette.text.primary + " !important",
          animationName: "none !important",
          animationDuration: "0s !important",
          animationDelay: "0s !important",
          transition: "background-color 99999s ease-in-out 0s !important",
        },
      },

      "& fieldset": {
        borderRadius: "999px",
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: valid
          ? theme.palette.input.borderColorHover
          : colors.error,
        borderWidth: !valid ? "1px" : undefined,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.input.borderColorHover,
        paddingRight: "20px",
      },

      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
        {
          WebkitAppearance: "none",
          margin: 0,
        },

      "& input[type=number]": {
        MozAppearance: "textfield",
      },
    },
  });

export const errorMessageSx: SxProps<Theme> = (theme: Theme) => ({
  color: colors.error,
  textAlign: "left",
});

export const infoMessageSx: SxProps<Theme> = (theme: Theme) => ({
  color: "#06B6D4",
  textAlign: "left",
  fontSize: "14px",
  lineHeight: 1.5,
});

export const passwordRequirementsTitleSx: SxProps<Theme> = {
  color: "#64748B",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: 1.5,
  textAlign: "left",
  mb: 0.5,
};

export const passwordRequirementsListSx: SxProps<Theme> = {
  m: 0,
  p: 0,
  listStyle: "none",
  textAlign: "left",
};

export const passwordRequirementsItemSx: SxProps<Theme> = {
  color: "#64748B",
  fontSize: "14px",
  lineHeight: 1.6,
};

export const passwordRequirementsItemRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  mb: 0.5,
  "&:last-of-type": {
    mb: 0,
  },
};

export const passwordRequirementsIconSx =
  (isMet: boolean, isUnmet: boolean): SxProps<Theme> => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    mt: "2px",
    flexShrink: 0,
    color: isMet ? "#16A34A" : isUnmet ? colors.error : "#64748B",
  });
